/**
 * Security — how ARA protects household information. Truthful, plain English.
 */
import {
  MarketingLayout, Eyebrow, Display, SectionTitle, FocalSentence,
  Body, Section, Hairline, SecondaryLink,
} from "./MarketingLayout";

function Block({ title, children, testid }) {
  return (
    <div className="max-w-[62ch]" data-testid={testid}>
      <SectionTitle>{title}</SectionTitle>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

export default function SecurityPage() {
  return (
    <MarketingLayout testid="security-page">
      <Section testid="security-hero" className="pt-24 sm:pt-32 pb-14">
        <div className="max-w-[820px]">
          <Eyebrow>Security</Eyebrow>
          <Display className="mt-6">Quietly careful.</Display>
          <FocalSentence className="mt-8 max-w-[46ch]">
            A home holds its family's private life. So does ARA. Here is how we look after it — honestly, without security theatre.
          </FocalSentence>
        </div>
      </Section>

      <Hairline />

      <Section testid="security-body" className="py-16 space-y-14">
        <Block title="Signing in" testid="security-auth">
          <Body>
            You sign in with your email address and a password. Passwords are never stored
            as you typed them — only in a one-way scrambled (hashed) form that we cannot
            reverse. Your session is kept alive with short-lived tokens that expire and
            refresh automatically.
          </Body>
        </Block>

        <Block title="Bank connections" testid="security-banking">
          <Body>
            ARA connects to New Zealand banks through Akahu, an established open-banking
            provider. ARA never sees or stores your internet banking password.
          </Body>
          <Body>
            The connection is read-only. ARA can see account names, balances and
            transactions. ARA cannot move money, make payments, or change anything at
            your bank.
          </Body>
          <Body>
            The tokens that keep the connection alive live only on our servers, as
            protected configuration. They are never sent to your browser, never written
            into the pages you load, and never appear in our code repository.
          </Body>
        </Block>

        <Block title="What ARA can and cannot access" testid="security-access">
          <Body>
            ARA can access what you put into your Residence, and — if you connect a bank —
            your account and transaction information.
          </Body>
          <Body>
            ARA cannot access your internet banking login, your email inbox, your
            contacts, or anything on your device you haven't shared.
          </Body>
        </Block>

        <Block title="How your information travels" testid="security-transit">
          <Body>
            Everything between your device and ARA travels over an encrypted connection
            (HTTPS). Your household's information is stored separately from every other
            household's, and every request is checked to make sure it belongs to yours.
          </Body>
        </Block>

        <Block title="If you disconnect your bank" testid="security-disconnect">
          <Body>
            Syncing stops immediately. Transactions already in your Study stay there so
            your record remains complete — until you ask us to remove them, which we will.
          </Body>
        </Block>

        <Block title="Deleting everything" testid="security-deletion">
          <Body>
            You can request full deletion of your account and household information at any
            time through the <SecondaryLink to="/contact">Contact page</SecondaryLink>.
            We confirm when it's done.
          </Body>
        </Block>

        <Block title="Found something?" testid="security-report">
          <Body>
            If you believe you've found a security problem in ARA, please tell us
            privately through the <SecondaryLink to="/contact">Contact page</SecondaryLink>{" "}
            before sharing it anywhere else. We take every report seriously and reply personally.
          </Body>
          <Body style={{ color: 'var(--ara-ink-soft, #7A7772)' }}>
            We deliberately avoid phrases like "bank-grade" or "military-grade" security.
            We'd rather tell you plainly what we do, and keep doing it carefully.
          </Body>
        </Block>
      </Section>
    </MarketingLayout>
  );
}
